/**
 * Cache (localStorage, por usuário) dos códigos de verificação exibidos.
 *
 * - `active`: códigos na tela, restaurados ao recarregar a página.
 * - `dismissed`: códigos fechados pelo usuário, para não voltarem quando os
 *   pendentes forem buscados de novo no backend.
 *
 * Tudo expira junto com o código (15 min), então o cache se limpa sozinho.
 */

export const VERIFICATION_CODE_TTL_MS = 15 * 60 * 1000;

export interface CachedVerificationCode {
  key: string;
  code: string;
  brand?: string | null;
  idUnitConsumer?: string | null;
  unitConsumerName?: string | null;
  receivedAt: string;
  expiresAt: string;
}

interface CacheData {
  active: CachedVerificationCode[];
  dismissed: Record<string, string>;
}

const storageKey = (userId: string) => `sv:verificationCodes:${userId}`;

export const verificationCodeKey = (idUnitConsumer?: string | null, receivedAt?: string | null) =>
  `${idUnitConsumer || ''}:${receivedAt || ''}`;

export const expiresAtFrom = (receivedAt: string) =>
  new Date(new Date(receivedAt).getTime() + VERIFICATION_CODE_TTL_MS).toISOString();

const isAlive = (expiresAt: string, now: number) => new Date(expiresAt).getTime() > now;

const read = (userId: string): CacheData => {
  const now = Date.now();
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const data = raw ? JSON.parse(raw) : null;
    const active: CachedVerificationCode[] = Array.isArray(data?.active) ? data.active : [];
    const dismissed: Record<string, string> =
      data?.dismissed && typeof data.dismissed === 'object' ? data.dismissed : {};

    return {
      active: active.filter((item) => item && item.key && isAlive(item.expiresAt, now)),
      dismissed: Object.fromEntries(
        Object.entries(dismissed).filter(([, expiresAt]) => isAlive(expiresAt, now))
      ),
    };
  } catch (error) {
    return { active: [], dismissed: {} };
  }
};

const write = (userId: string, data: CacheData) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(data));
  } catch (error) {
    // cache é acessório: sem storage o toast continua funcionando
  }
};

export const getActiveCodes = (userId: string) => {
  const data = read(userId);
  return data.active.filter((item) => !data.dismissed[item.key]);
};

export const isDismissed = (userId: string, key: string) => !!read(userId).dismissed[key];

export const saveActiveCode = (userId: string, item: CachedVerificationCode) => {
  const data = read(userId);
  if (data.dismissed[item.key]) return;
  write(userId, {
    ...data,
    active: [item, ...data.active.filter((current) => current.key !== item.key)],
  });
};

export const dismissCode = (userId: string, key: string, expiresAt: string) => {
  const data = read(userId);
  write(userId, {
    active: data.active.filter((item) => item.key !== key),
    dismissed: { ...data.dismissed, [key]: expiresAt },
  });
};
